import React, { useState, useCallback } from "react";
import {
    Sidebar,
    Menu,
    Image,
    Grid,
    Icon,
    Divider,
    Popup,
    Header
} from "semantic-ui-react";
import { NavLink as Link } from "react-router-dom";
import { LogoTextWhite, LogoWhite, ButtonSquare, NavCard, PRODUCT_PERMISSIONS, USER_PERMISSIONS, DateTimeUtils, multipleRestriction, RestrictView } from 'dyl-components';
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import leftSidebarUiActions from "actions/left_sidebar_ui";
import office_view from 'actions/office_view';
import RestrictedView from 'shared/RestrictedView';
import { QueryParamsUtils } from "utils";


const LeftSidebar = ({ isSidebarOpen, onToggleSecondarySidebar, onCloseSecondarySidebar, isActiveSecondarySidebar, isTextFormDirty, setIsConfirmationOpen, setConfirmAction, isDisableConfirmationForPopups  }) => {
    const dispatch = useDispatch();

    const { userPermissions, activeSubMenuItem } = useSelector((state) => {  
        return {
            userPermissions: state.role.userPermissions.permission,
            activeSubMenuItem: state.left_sidebar_ui.activeSubMenuItem,
        }
    });
    
    const user_id = useSelector(state => state?.auth?.user_id);
    const userAuthTimezone = useSelector((state) => state.auth.timezone) || DateTimeUtils.getTimeZone();

    const [isReportsOpen, setIsReportsOpen] = useState(false);

    const onToggleReports = useCallback(() => {
        setIsReportsOpen(prev => !prev);
    }, []);

    const onClosePhoneNotifications = () => {
        dispatch(office_view.onClosePhoneNotifications("CLOSE_CALL_NOTIFICATIONS"));
    }

    const createButtonProperties = useCallback(
        name => ({
            active: false,
            onClick: () => {
                 if (isTextFormDirty && !isDisableConfirmationForPopups) {
                    setConfirmAction(() => () => {
                        dispatch(leftSidebarUiActions.onSetMenuActive(name));
                    });
                    setIsConfirmationOpen(true);
                    return;
                } else {
                    dispatch(leftSidebarUiActions.onSetMenuActive(name));
                    onToggleSecondarySidebar(name);
                }
            },
            className: "DYL2Menu__item DYL2Menu__item"
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, onToggleSecondarySidebar]
    );

    const renderMenuItem = (
        path,
        id,
        icon,
        name,
        userPermission,
        isNavCard,
        iconClassName
    ) => {
        if (isNavCard) {
            return (
                <Link
                    key={id}
                    style={{ color: "inherit", textDecoration: "inherit" }}
                    to={path}
                    onClick={() => {
                        if (isActiveSecondarySidebar?.("view") && !isTextFormDirty) {
                            onCloseSecondarySidebar?.();
                        }
                        onToggleReports();
                    }}
                >
                    <RestrictedView userPermission={userPermission} type="hidden">
                        <NavCard
                            icon={icon}
                            header={name}
                            description=""
                            hsize="h6"
                            noPadding
                            iconClassName={iconClassName}
                        />
                    </RestrictedView>
                </Link>
            );
        }

        return (
            <Menu.Item
                key={id}
                className={`DYL2Menu__item DYL2Menu__subitem DYL2Menu__subitem${activeSubMenuItem === id ? "--active" : ""
                    }`}
                as={Link}
                to={path}
                onClick={() =>
                    dispatch(leftSidebarUiActions.onSetSubMenuActive(id))
                }
            >
                <Icon
                    name={icon}
                    color={activeSubMenuItem === id && "yellow"}
                    className="Menu__icon"
                />
                {isSidebarOpen ? name : ""}
            </Menu.Item>
        );
    };

    const img = isSidebarOpen
        ? { src: LogoTextWhite }
        : { src: LogoWhite };

    const headerMenuItems = [
        { id: "favorites", icon: "star outline", name: "Favorites", left: 115 },
        { id: "recents", icon: "fas fa-history", name: "Recents", left: 115 }
    ];

    const topMenuItems = [
        { id: "dashboard", icon: "dashboard", name: "Dashboard", path: "/", left: 115, end: true }
    ];

    const midMenuItems = [
        { id: "contacts", icon: "fas fa-user", name: "Contacts", path: "/contacts", left: 105, userPermission: multipleRestriction([USER_PERMISSIONS.CONTACTS_VIEW], userPermissions)},
        { id: "leads", icon: "fas fa-address-card", name: "Leads", path: "/leads", left: 85, userPermission: multipleRestriction([USER_PERMISSIONS.LEAD_VIEW], userPermissions)},
        { id: "opportunities", icon: "fas fa-funnel-dollar", name: "Opportunities", path: "/opportunities", left: 135, userPermission: multipleRestriction([USER_PERMISSIONS.OPPORTUNITY_VIEW], userPermissions)},
        { id: "accounts", icon: "fas fa-house-building", name: "Accounts", path: "/accounts", left: 135, userPermission: multipleRestriction([USER_PERMISSIONS.ACCOUNT_VIEW, USER_PERMISSIONS.MASTER_ACCOUNT_VIEW], userPermissions) }
    ];

    const lowerMenuItems = [
        { id: 'campaigns', icon: 'fa-solid fa-megaphone', name: 'Campaigns', path: '/campaigns' },
        { id: "groups", icon: "folder", name: "Groups", path: "/groups", left: 140 },
        { id: "calendar", icon: "fas fa-calendar-alt", name: "Calendar", path: "/calendar", left: 105 }
    ];

    const reportsMenu = [
        { id: "call_history", path: "/call-history", name: "Call History", icon: "call", iconClassName: "fa-solid fa-phone", userPermission: USER_PERMISSIONS.CALL_HISTORY_VIEW },
        { id: 'text_log', path: '/text-history', name: 'Text History', icon: 'fas fa-comment', left: 97, userPermission: USER_PERMISSIONS.TEXTING_HISTORY_VIEW},
        { id: "quotes_orders", path: "/quotes-orders", name: "Quotes and Orders", icon: "fas fa-box-dollar" },
        { id: "invoices", path: "/invoices", name: "Invoices", icon: "fas fa-file-invoice-dollar" },
        { id: "sequence_reports", path: `/sequence-reports?user_id=${user_id}&${QueryParamsUtils.getDayTimestampsParameter(userAuthTimezone)}`, name: "Sequence Reports", icon: "fas fa-network-wired" },
        { id: 'pipeline_reports', path: `/pipeline-reports`, name: 'Pipeline Reports', icon: 'fa-solid fa-filter-list' }
    ];

    const hasUnreadNotification = false;    

    const collapseOverlapingMenu = () => {
        onCloseSecondarySidebar?.();
    };

    return (
        <Sidebar
            as={Menu}
            inverted
            visible
            className={`DYL2Menu DYL2Menu--${isSidebarOpen ? "open" : "closed"} DYL2Menu--${isReportsOpen ? "scroll" : ""
                }`}
        >
            <div className="DYL2Logo">
                <Image src={img.src} />
            </div>

            <Grid className="DYL2MenuGrid" container stackable>
                 <Grid.Row columns={isSidebarOpen ? 3 : 1} style={{ padding: 0, marginTop: isSidebarOpen ? 0 : 7}}>                        
                        <Grid.Column textAlign={isSidebarOpen ? 'left' : 'center'} style={isSidebarOpen ? { paddingLeft: 20, paddingRight: 0, paddingTop: 7, paddingBottom: 20 } : { paddingLeft: 0.1, paddingRight: 0, paddingBottom: 6 }}>  
                            <RestrictedView type='disabled' productPermission={PRODUCT_PERMISSIONS.OFFICE_VIEW}>
                                <ButtonSquare
                                    text="View"
                                    icon="fa-solid fa-phone-office"
                                    onClick={() => { 
                                        if (isTextFormDirty && !isDisableConfirmationForPopups) {
                                            setConfirmAction(() => () => {
                                                onToggleSecondarySidebar("view"); 
                                                onClosePhoneNotifications();
                                            });
                                            setIsConfirmationOpen(true);
                                            return;
                                        } else {
                                            onToggleSecondarySidebar("view"); 
                                            onClosePhoneNotifications();
                                        }
                                    }}
                                />
                            </RestrictedView>
                        </Grid.Column>
                        <Grid.Column textAlign={isSidebarOpen ? 'left' : 'center'} style={isSidebarOpen ? { paddingLeft: 28, paddingRight: 0, paddingTop: 7, paddingBottom: 20 } : { paddingLeft: 0.1, paddingRight: 0, paddingBottom: 20 }}>
                                {hasUnreadNotification && <div className="TopNavigation__Texting--Dot" />}
                                <RestrictedView type='disabled' productPermission={PRODUCT_PERMISSIONS.TEXT_VIEW}>
                                    <ButtonSquare
                                        text="Text"
                                        icon="fa-solid fa-comments"
                                        onClick={() => {
                                             if (isTextFormDirty && !isDisableConfirmationForPopups) {
                                            setConfirmAction(() => () => {
                                                onToggleSecondarySidebar("text");

                                            });
                                            setIsConfirmationOpen(true);
                                            return;
                                        } else {
                                            onToggleSecondarySidebar("text");

                                        }
                                        }}
                                    />
                            </RestrictedView>
                        </Grid.Column>
                    </Grid.Row>
            </Grid>

                {headerMenuItems.map(({ id, icon, name, left }) =>
                    !isSidebarOpen ? (
                        <Popup
                            trigger={
                                <Menu.Item
                                    {...createButtonProperties(id)}
                                >
                                    <Icon name={icon} className="Menu__icon" />
                                </Menu.Item>
                            }
                            content={name}
                            inverted
                            style={{ left: isReportsOpen ? left : 0 }}
                            position={isReportsOpen ? "left center" : "right center"}
                        />
                    ) : (
                        <Menu.Item
                            {...createButtonProperties(id)}

                        >
                            <Icon name={icon} className="Menu__icon" />
                            {name}
                        </Menu.Item>
                    )
                )}

                {isSidebarOpen && <Divider className='Menu__Divider' />}
                {topMenuItems.map(({ id, icon, name, path, left, end }) =>
                    !isSidebarOpen ? (
                        <Popup
                            trigger={
                                <Menu.Item
                                    as={Link}
                                    to={path}
                                    onClick={collapseOverlapingMenu}
                                >
                                    <Icon name={icon} className="Menu__icon" />
                                </Menu.Item>
                            }
                            content={name}
                            inverted
                            style={{ left: isReportsOpen ? left : 0 }}
                            position={isReportsOpen ? "left center" : "right center"}
                        />
                    ) : (
                        <Menu.Item
                            as={Link}
                            to={path}
                            end={end}
                            onClick={collapseOverlapingMenu}
                        >
                            <Icon name={icon} className="Menu__icon" />
                            {name}
                        </Menu.Item>
                    )
                )}

                {isSidebarOpen && <Divider className='Menu__Divider' />}
                {midMenuItems.map(({ id, icon, name, path, left, userPermission }) => 
                    !isSidebarOpen ? (
                        <RestrictView type="hidden" isRestricted={userPermission}>
                            <Popup
                                trigger={
                                        <Menu.Item
                                            as={Link}
                                            to={path}
                                            onClick={collapseOverlapingMenu}
                                        >
                                            <Icon name={icon} className="Menu__icon" />
                                        </Menu.Item>
                                }
                                content={name}
                                inverted
                                style={{ left: isReportsOpen ? left : 0 }}
                                position={isReportsOpen ? "left center" : "right center"}
                            />
                        </RestrictView>
                    ) : (
                        <RestrictView type="hidden" isRestricted={userPermission}>
                            <Menu.Item
                                as={Link}
                                to={path}
                                onClick={collapseOverlapingMenu}
                            >
                                <Icon name={icon} className="Menu__icon" />
                                {name}
                        </Menu.Item>
                        </RestrictView>
                    )
                )}

                {isSidebarOpen && <Divider className='Menu__Divider' />}
                {lowerMenuItems.map(({ id, icon, name, path, left }) =>
                    !isSidebarOpen ? (
                        <Popup
                            trigger={
                                <Menu.Item
                                    as={Link}
                                    to={path}
                                    onClick={collapseOverlapingMenu}
                                >
                                    <Icon name={icon} className="Menu__icon" />
                                </Menu.Item>
                            }
                            content={name}
                            inverted
                            style={{ left: isReportsOpen ? left : 0 }}
                            position={isReportsOpen ? "left center" : "right center"}
                        />
                    ) : (
                        <Menu.Item
                            as={Link}
                            to={path}
                            onClick={collapseOverlapingMenu}
                        >
                            <Icon name={icon} className="Menu__icon" />
                            {name}
                        </Menu.Item>
                    )
                )}

                {isSidebarOpen && <Divider className='Menu__Divider' />}

            <Popup
                trigger={!isSidebarOpen ? <Menu.Item
                    className="DYL2Menu__item"
                    onClick={onToggleReports}
                >
                    <Icon.Group className="Menu__icon">
                        <Icon name="pie chart" />{" "}
                        <Icon name={`caret ${isReportsOpen ? 'left' : 'right'}`} />
                    </Icon.Group>
                </Menu.Item> : <Menu.Item
                    className='DYL2Menu__item'
                    onClick={onToggleReports}
                >
                    <Icon name={'pie chart'} className="Menu__icon" />
                    Reports/Logs
                    <Icon name={`caret ${isReportsOpen ? 'left' : 'right'}`} style={{ position: 'relative', left: '1.4em' }} />
                </Menu.Item>}
                open={isReportsOpen}
                position="right center"
                onClose={() => setIsReportsOpen(false)}
                on="click"
                basic
                size="huge"
                style={{ padding: "14px" }}
                offset={[100, -10]}
                content={
                    <div className="DYL2Menu__reports-logs">
                        <Grid columns="equal">
                            <Grid.Column>
                                <Header as="h3">Logs</Header>
                                {reportsMenu.slice(0, 2).map(item =>
                                    renderMenuItem(
                                        item.path,
                                        item.id,
                                        item.icon,
                                        item.name,
                                        item.userPermission,
                                        true,
                                        item.iconClassName
                                    )
                                )}
                            </Grid.Column>
                            <Grid.Column>
                                <Header as="h3">Reports</Header>
                                {reportsMenu.slice(2).map(item =>
                                    renderMenuItem(
                                        item.path,
                                        item.id,
                                        item.icon,
                                        item.name,
                                        item.userPermission,
                                        true,
                                        item.iconClassName
                                    )
                                )}
                            </Grid.Column>
                        </Grid>
                    </div>
                }
            />
        </Sidebar>
    );
};

export default LeftSidebar;
